import React, { useEffect, useMemo, useState } from 'react';
import { Check, X, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useLocale, useT } from '../../contexts/LocaleProvider';
import { deptLabel } from '../../lib/departments';
import api from '../../utils/api';

const KIND_VALUES = ['public_holiday', 'closure', 'team_event', 'absence'];

const emptyDraft = () => ({
  title: '', description: '', start_date: '', end_date: '',
  kind: 'team_event', half_day: '', departement: '', employee_email: '',
});

const HALF_DAY_VALUES = ['', 'am', 'pm'];

export default function EventComposer({ existingEmployees = [], initial, onSave, onCancel, onDelete, mode = 'create', isHR = true }) {
  const t = useT();
  const { locale } = useLocale();
  const [draft, setDraft] = useState(() => initial ? { ...emptyDraft(), ...initial } : emptyDraft());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // For non-HR users the composer is locked to "share my own absence" mode:
  // kind = absence, employee = self, no team-wide options.
  const lockedToOwnAbsence = !isHR;

  // Default end_date to start_date when the user picks start first
  useEffect(() => {
    if (draft.start_date && !draft.end_date) {
      setDraft(d => ({ ...d, end_date: d.start_date }));
    }
  }, [draft.start_date, draft.end_date]);

  // Half-day only makes sense on a single date; clear it on multi-day ranges
  useEffect(() => {
    if (draft.start_date && draft.end_date && draft.start_date !== draft.end_date && draft.half_day) {
      setDraft(d => ({ ...d, half_day: '' }));
    }
  }, [draft.start_date, draft.end_date, draft.half_day]);

  // On create, prefill the employee field with the logged-in user. HR can
  // still pick someone else from the dropdown if they're logging an absence
  // on behalf of a colleague. Non-HR users get kind=absence forced too.
  useEffect(() => {
    if (mode !== 'create') return;
    let cancelled = false;
    api.myEmployee().then(me => {
      if (cancelled || !me?.email) return;
      setDraft(d => ({
        ...d,
        employee_email: d.employee_email || me.email,
        kind: lockedToOwnAbsence ? 'absence' : d.kind,
      }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [mode, lockedToOwnAbsence]);

  const departments = useMemo(() => {
    const set = new Set();
    for (const e of existingEmployees) if (e.departement) set.add(e.departement);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [existingEmployees]);

  const submit = async () => {
    if (!draft.title.trim() || !draft.start_date || !draft.end_date) {
      setError(t('calendar.f.required'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onSave(draft);
    } catch (e) {
      const msg = e.response?.data;
      setError(typeof msg === 'string' ? msg
        : msg?.detail || Object.values(msg || {}).flat().join(' · ') || t('editor.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5 mb-5">
      <h2 className="text-[15px] font-semibold mb-4">
        {mode === 'edit' ? t('calendar.eventEdit') : t('calendar.eventNew')}
      </h2>

      {error && (
        <div className="mb-3 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-[14px] rounded-md">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="field md:col-span-2">
          <label className="field-label">{t('calendar.f.title')} *</label>
          <input className="input" value={draft.title}
            onChange={e => setDraft({ ...draft, title: e.target.value })}
            placeholder={t('calendar.f.title.ph')} />
        </div>
        <div className="field">
          <label className="field-label">{t('calendar.f.start')} *</label>
          <input className="input" type="date" value={draft.start_date}
            onChange={e => setDraft({ ...draft, start_date: e.target.value })} />
        </div>
        <div className="field">
          <label className="field-label">{t('calendar.f.end')} *</label>
          <input className="input" type="date" value={draft.end_date}
            onChange={e => setDraft({ ...draft, end_date: e.target.value })} />
        </div>
        {draft.start_date && draft.start_date === draft.end_date && (
          <div className="field md:col-span-2">
            <label className="field-label">{t('calendar.f.halfDay')}</label>
            <div className="inline-flex items-center border border-border rounded-md overflow-hidden">
              {HALF_DAY_VALUES.map((v, i) => (
                <button
                  key={v || 'full'}
                  type="button"
                  onClick={() => setDraft({ ...draft, half_day: v })}
                  className={[
                    'px-3 py-1.5 text-[13px] transition',
                    i > 0 ? 'border-l border-border' : '',
                    draft.half_day === v
                      ? 'bg-[hsl(var(--text))] text-[hsl(var(--bg))]'
                      : 'text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--surface-2))]',
                  ].join(' ')}
                  aria-pressed={draft.half_day === v}
                >
                  {t(`calendar.halfDay.${v || 'full'}`)}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="field">
          <label className="field-label">{t('calendar.f.kind')}</label>
          <select value={draft.kind}
            disabled={lockedToOwnAbsence}
            onChange={e => setDraft({ ...draft, kind: e.target.value })}>
            {KIND_VALUES.map(k => <option key={k} value={k}>{t(`calendar.kind.${k}`)}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="field-label">
            {t('calendar.f.dept')} <span className="text-[hsl(var(--text-muted))]">{t('common.optional')}</span>
          </label>
          <select value={draft.departement} onChange={e => setDraft({ ...draft, departement: e.target.value })}>
            <option value="">{t('calendar.f.deptAll')}</option>
            {departments.map(d => <option key={d} value={d}>{deptLabel(d, locale)}</option>)}
          </select>
        </div>
        {draft.kind === 'absence' && (
          <div className="field md:col-span-2">
            <label className="field-label">{t('calendar.f.employee')}</label>
            <select value={draft.employee_email}
              disabled={lockedToOwnAbsence}
              onChange={e => setDraft({ ...draft, employee_email: e.target.value })}>
              <option value="">{t('common.none')}</option>
              {existingEmployees.map(emp => (
                <option key={emp.id} value={emp.email}>{emp.full_name} ({emp.email})</option>
              ))}
            </select>
          </div>
        )}
        <div className="field md:col-span-2">
          <label className="field-label">
            {t('calendar.f.desc')} <span className="text-[hsl(var(--text-muted))]">{t('common.optional')}</span>
          </label>
          <textarea rows={2} value={draft.description}
            onChange={e => setDraft({ ...draft, description: e.target.value })}
            placeholder={t('calendar.f.desc.ph')} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-4">
        {mode === 'edit' && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(initial)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-[13px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))] mr-auto"
          >
            <Trash2 className="h-3.5 w-3.5" /> {t('common.delete')}
          </button>
        )}
        <Button variant="outline" size="sm" onClick={onCancel} disabled={busy} className="gap-1.5">
          <X className="h-3.5 w-3.5" /> {t('common.cancel')}
        </Button>
        <Button size="sm" onClick={submit} disabled={busy} className="gap-1.5">
          <Check className="h-3.5 w-3.5" /> {mode === 'edit' ? t('common.save') : t('common.add')}
        </Button>
      </div>
    </section>
  );
}
